import { Fragment, useCallback, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useDebounce } from 'react-use';

import { rage, useInputAction, useObservable } from '~/rage';
import {
	IModalReplyWithValue,
	InputAction,
	InputKey,
	IVehicleShopVehicle,
	ModalButtonColor,
	ModalType,
	WalletType,
} from '~/shared';

import { VehicleStats } from '../../components/VehicleStats';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { setVehicleSpecsExpanded } from '../../redux/slices/menuPanelsSlice';
import { setParkingSlotsErrorVisible } from '../../redux/slices/playerSlice';
import {
	setMaxIndex,
	setSelectedIndexNext,
	setSelectedIndexPrev,
	setSelectedVehicle,
} from '../../redux/slices/vehicleSelectionSlice';
import { AcquireInfoSelection } from '../AcquireInfoSelection';
import {
	StyledInterfaceContainer,
	StyledListLabel,
	StyledMenuControlsLeft,
	StyledMenuControlsRight,
	StyledShopLogo,
	StyledVehicleList,
	StyledVehicleListControls,
} from './VehicleSelection.styled';

const VehicleSelection = () => {
	const [currentVehicle, setCurrentVehicle] =
		useState<IVehicleShopVehicle | null>(null);
	const vehicleSpecsExpanded = useLayoutSelector(
		(state) => state.menuPanels.expanded.vehicleSpecs,
	);
	const shopVehicles =
		useObservable(rage.vehicleShop.getShopInfo())?.vehicles || [];
	const selectedVehicleIndex = useLayoutSelector(
		(state) => state.vehicleSelection.selectedIndex,
	);
	const interfaceHidden = useLayoutSelector(
		(state) => state.menuPanels.interfaceHidden,
	);
	const shopInfo = useObservable(rage.vehicleShop.getShopInfo());
	const selectedColor = useLayoutSelector(
		(state) => state.colorPicker.selectedColor,
	);
	const currentModal = useObservable(rage.modal.getCurrentModal());
	const availableSlots = useObservable(rage.player.getTotalParkingSlots());
	const usedSlots = useObservable(rage.player.getUsedParkingSlots());
	const dispatch = useLayoutDispatch();

	useDebounce(
		() => {
			const shopVehicle = shopVehicles[selectedVehicleIndex];
			rage.vehicleShop
				.setPreviewVehicleModel(shopVehicle?.model || null)
				.then(() => rage.vehicleShop.setPreviewVehicleColor(selectedColor))
				.catch((err) => rage.notifications.showException(err));
		},
		250,
		[selectedVehicleIndex, shopVehicles],
	);

	useEffect(() => {
		setCurrentVehicle(shopVehicles[selectedVehicleIndex]);
	}, [selectedVehicleIndex, shopVehicles]);

	useEffect(() => {
		dispatch(setMaxIndex(shopVehicles.length - 1));
	}, [shopVehicles]);

	const handleVehicleAction = useCallback(
		(actionName: string, walletType?: WalletType) => {
			if (currentModal) {
				return;
			}

			if (actionName === 'buy') {
				dispatch(setSelectedVehicle(currentVehicle));
				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: `Вы действительно хотите купить транспорт ${currentVehicle?.name}?`,
						price: currentVehicle?.priceCash || 0,
						priceDonate: currentVehicle?.priceDonate || 0,
						walletTypes: [walletType || WalletType.Cash, WalletType.Donate],
						acceptButton: { text: 'Купить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result: IModalReplyWithValue<WalletType | null>) => {
						if (result.isAccepted && currentVehicle !== null) {
							if (usedSlots >= availableSlots) {
								dispatch(setParkingSlotsErrorVisible(true));
								setTimeout(() => {
									dispatch(setParkingSlotsErrorVisible(false));
								}, 3000);
								return;
							}
							rage.vehicleShop
								.buyVehicle(
									currentVehicle.model,
									selectedColor,
									result.value || WalletType.Cash,
								)
								.then(() => dispatch(setSelectedVehicle(null)))
								.catch((err) => rage.notifications.showException(err));
						}
					},
				);
			} else if (actionName === 'test-drive') {
				if (!currentVehicle) return;
				rage.modal.showModal(
					{
						type: ModalType.VehicleTestDrive,
						durationSec: shopInfo?.testDriveDurationSec || 0,
						price: currentVehicle.priceTestDrive,
						wallet: WalletType.Cash,
						vehicleName: currentVehicle.name,
					},
					async (result) => {
						if (!result.isAccepted) return;
						if (currentVehicle === null) return;
						await rage.vehicleShop.testDriveVehicle(
							currentVehicle.model,
							selectedColor,
						);
					},
				);
			}
		},
		[currentVehicle, selectedColor, shopInfo, currentModal],
	);

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (currentModal) {
			return;
		}

		dispatch(setSelectedIndexPrev());
	});
	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (currentModal) {
			return;
		}

		dispatch(setSelectedIndexNext());
	});

	const handleShowVehicleSpecs = () => {
		dispatch(setVehicleSpecsExpanded(true));
	};
	useInputAction(InputAction.CommonMenuToggleInfo, () => {
		if (currentModal) {
			return;
		}

		handleShowVehicleSpecs();
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (currentModal) {
			return;
		}
		handleVehicleAction('buy', WalletType.Cash);
	});

	const leftControls = [
		{
			text: 'Управление',
			keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
		},
		{
			text: 'Купить транспорт',
			keys: [InputKey.Enter],
			onClick: () => handleVehicleAction('buy', WalletType.Cash),
		},
		{
			text: 'Об автомобиле',
			keys: [InputKey.I],
			visible: !vehicleSpecsExpanded,
			onClick: handleShowVehicleSpecs,
		},
		{
			text: 'Крутить транспорт',
			keys: [InputKey.MouseRight],
		},
	];

	const handleMenuClose = useCallback(() => {
		if (currentModal) {
			return;
		}
		rage.vehicleShop
			.exitShop()
			.catch((err) => rage.notifications.showException(err));
	}, [currentModal]);
	useInputAction(InputAction.CommonMenuClose, () => {
		handleMenuClose();
	});

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: handleMenuClose,
		},
	];

	return (
		<Fragment>
			<CSSTransition
				in={interfaceHidden}
				classNames={'interface'}
				timeout={500}
			>
				<StyledInterfaceContainer>
					<StyledShopLogo />
					<AcquireInfoSelection onActionButtonClick={handleVehicleAction} />
					<VehicleStats />
					<StyledListLabel>Все товары</StyledListLabel>
					<StyledVehicleListControls />
					<StyledVehicleList />
				</StyledInterfaceContainer>
			</CSSTransition>
			<StyledMenuControlsLeft controls={leftControls} />
			<StyledMenuControlsRight controls={rightControls} />
		</Fragment>
	);
};

export { VehicleSelection };
